import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../services/apiService";
import { QuestionResponse, QuestionRequest, SurveyState, AnswerRequest, SubmitRequest, SubmitResponse } from "../../types";
import { store, RootState } from "../store";

import { apiEndpoints } from "../../config/apiEndpoints";
import { currentEnv } from "../../config/config";
import { isAxiosError } from "axios";

const initialState: SurveyState = {
  survey: null,
  loading: false,
  error: null,
  docusignUri: null,
  allAnswersDeclined: null
};

/**
 * Asynchronous thunk action to fetch the next or previous survey question.
 *
 * This function determines whether to fetch data from a mock source (in development mode)
 * or from an API endpoint (in production mode). In development, it simulates a network delay
 * and retrieves mock question data based on the current order and submission type.
 *
 * @param {Partial<QuestionRequest>} survey - The survey request containing details like QuestionOrder and SubmitType.
 * @returns {Promise<QuestionResponse>} A promise resolving to the next or previous question response.
 */

export const getQuestion = createAsyncThunk(

  "survey/getQuestion",

  async (survey: Partial<QuestionRequest>) => {
    console.log("Environment:", currentEnv);
    //Change to "VERSION: MOCK"
    if (currentEnv == "labs") {
      const mockResponse = await apiClient.post(
        apiEndpoints.getQuestion,
        survey,
      );
      //function that calls allQuestions, pass next question to this function
      function getMockData(currentQuestionOrder: number | undefined = 0, submitType: string | undefined, allQuestions: QuestionResponse[]): QuestionResponse | undefined {
        console.log("GetMockData Environment:", currentEnv);
        if (allQuestions && currentQuestionOrder && submitType) {

          if (currentQuestionOrder == 0) {
            return allQuestions[0];
          }

          const currentIndex = allQuestions.findIndex(
            (q) => q.QuestionOrder === currentQuestionOrder
          );

          if (submitType === "Next" && currentIndex !== -1 && currentIndex < allQuestions.length - 1) {
            console.log("+ 1: ", allQuestions[currentIndex + 1])
            return allQuestions[currentIndex + 1];
          } else if (submitType === "Previous" && currentIndex > 0) {
            console.log("-1 : ", allQuestions[currentIndex - 1])
            return allQuestions[currentIndex - 1];
          } else if (submitType === "Next" && currentIndex === allQuestions.length - 1) {
            // Still have to solve the IsFinalQuestion = true; 
            return { IsFinalQuestion: true } as QuestionResponse;
          } else if (submitType === "Previous" && currentIndex === 0) {
            // temporary situation
            return allQuestions[0];
          } else {
            const current = allQuestions.find(q => q.QuestionOrder === currentQuestionOrder);
            return current || { Message: "Question not found in mock data" } as QuestionResponse;
          }
        } else {
          return { Message: "Invalid request in mock environment" } as QuestionResponse;
        }
      }

      const allQuestions: QuestionResponse[] = mockResponse.data.data;
      const currentQuestionOrder = survey.QuestionOrder;
      const submitType = survey.SubmitType;

      const response = getMockData(currentQuestionOrder, submitType, allQuestions) as QuestionResponse;

      return response
      // VERSION = /V --- if it is NOT using mock data, the endpoints uses  /api/v*
    } else if (currentEnv == "development" || currentEnv == "production") {
      const response = await apiClient.post(
        apiEndpoints.getQuestion,
        survey,
      );
      return response.data as QuestionResponse;
    }
    return { Message: "Environment not handled" } as QuestionResponse;
  }
);

/**
 * Asynchronous thunk action to save a survey answer.
 *
 * This function saves the user's answer to the survey question. In development mode,
 * it simulates a network delay and logs the data to the console. In production,
 * it sends the answer to an API endpoint.
 *
 * @param {Partial<AnswerRequest>} survey - The answer request containing the user's answer and related details.
 * @returns {Promise<boolean|Response>} A promise resolving to true in development or the API response in production.
 */
export const saveAnswer = createAsyncThunk(
  "survey/saveAnswer",
  async (survey: Partial<AnswerRequest>) => {
    const { AllAnswersDeclined, ...formAnswers } = survey
    const response = await apiClient.post(
      apiEndpoints.saveAnswer,
      formAnswers,
    );

    return { response: response.data, AllAnswersDeclined };
  }
);

/**
 * Asynchronous thunk action to submit the completed survey.
 *
 * This function informs the API that the survey was completed. In development mode,
 * it simulates a network delay and logs a message to the console. In production,
 * it sends the session data to an API endpoint.
 *
 * @param {SubmitRequest} sessionData - The session data (SessionId, ClientId).
 * @returns {Promise<boolean|Response>} A promise resolving to true in development or the API response in production.
 */
export const submitSurvey = createAsyncThunk(
  "survey/submitAnswer",
  async (sessionData: SubmitRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        apiEndpoints.submitSurvey,
        sessionData
      );
      return {response: response.data as SubmitResponse, Decline: sessionData.Decline};
    } catch (error) {
     if (isAxiosError(error) && error.response) {
        // Correctly capturing the API error data
        return rejectWithValue(error.response.data);
      }
      // For any other unexpected errors
      return rejectWithValue("An unknown error occurred.");
    }
  }
);

/**
 * Asynchronous thunk action creator to submit an answer and fetch the next question.
 *
 * This function dispatches two asynchronous thunks in sequence: `saveAnswer` and `getQuestion`.
 * It first dispatches `saveAnswer` to save the user's answer, and if successful,
 * it then dispatches `getQuestion` to fetch the next question.
 *
 * @async
 * @param {Partial<AnswerRequest>} answerData - The data required to save the user's answer.
 * @param {Partial<QuestionRequest>} questionData - The data required to fetch the next question.
 * @returns {Promise<void>} A promise that resolves when both `saveAnswer` and `getQuestion` have completed, or rejects if either fails.
 * @throws {Error} Throws an error if `saveAnswer` fails or if any other error occurs during the process.
 */
export const submitAnswerAndGetQuestion = (
  answerData: Partial<AnswerRequest>,
  questionData: Partial<QuestionRequest>
) => async (dispatch: typeof store.dispatch, _getState: () => RootState) => {
  try {
    console.log("AnswerData", answerData);
    const saveAnswerResult = await dispatch(saveAnswer(answerData));

    if (saveAnswerResult.meta.requestStatus === 'fulfilled') {
      try {
        await dispatch(getQuestion(questionData));

      } catch (error){
        console.error("Failed to fetch question", error)
      }
    } else {
      console.error("Failed to save answer:", saveAnswerResult.payload);
      throw new Error("Failed to save answer");
    }
  } catch (error) {
    console.error("Error during save answer:", error);
    throw new Error("Error during save answer");
  }
};



const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      //getQuestion
      .addCase(getQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.survey = action.payload;
      })
      .addCase(getQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch question";
      })
      //saveAnswer
      .addCase(saveAnswer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveAnswer.fulfilled, (state, action) => {
        state.loading = false;
        state.allAnswersDeclined = action.payload?.AllAnswersDeclined;
      })
      .addCase(saveAnswer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to send answers";
      })
      .addCase(submitSurvey.pending, (state) => {
        state.error = null;
        state.docusignUri = null;
      })
      .addCase(submitSurvey.fulfilled, (state, action) => {
        state.loading = false;
        state.allAnswersDeclined = action.payload?.Decline;
        if (action.payload && action.payload.response.DocuSignURI) {
          state.docusignUri = action.payload.response.DocuSignURI;
        }
      })
      .addCase(submitSurvey.rejected, (state, action) => {
        state.loading = false;
        state.docusignUri = null;
        const message = action.error.message || "Failed to submit survey";
        state.error = typeof message === 'string' ? message : "An API error occurred.";
      })
  },
});

export default surveySlice;
